import React, { useEffect, useLayoutEffect, useRef } from "react";
import PropTypes from "prop-types";
import Checklist from "./Checklist";
import { Button, ButtonVariant, IconNames } from "@wattpad/web-ui-library";
import { ConnectImage } from "../../../shared-components";
import EmailSignup from "../EmailSignup";
import { useTrans } from "../../../hooks";
import LoginForm from "../LoginForm";
import { SignupHeader } from "./SignupHeader";
import { AuthenticationOptions } from "./AuthenticationOptions";
import {
  sendLoginPromptViewEvent,
  sendSignupPromptViewEvent
} from "@wattpad/client-platform-web-events";
import { FORM_VIEW } from "../constants";

const sendPromptViewEvent = (view, eventDetails) => {
  // We don't care about the email signup form currently. Exit without event logging.
  if (view.previousView === FORM_VIEW.REGISTER) {
    return;
  }

  if (view.currentView === FORM_VIEW.REQUIRE_SIGNUP) {
    sendSignupPromptViewEvent(eventDetails);
  } else if (view.currentView === FORM_VIEW.LOGIN) {
    sendLoginPromptViewEvent(eventDetails);
  }
};

const updateFormView = setAuthFormView => {
  return view => {
    setAuthFormView(prev => {
      return {
        previousView: prev.currentView,
        currentView: view
      };
    });
  };
};

const RequireSignup = ({ nextUrl, eventSource }) => {
  const [authFormView, setAuthFormView] = React.useState({
    previousView: undefined,
    currentView: FORM_VIEW.REQUIRE_SIGNUP
  });
  const { trans } = useTrans();
  const authFormRef = useRef(null);

  const handleFormViewUpdate = updateFormView(setAuthFormView);

  useEffect(
    () => {
      if (!authFormRef.current) return;

      const currentRef = authFormRef.current;

      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            sendPromptViewEvent(authFormView, eventSource);
            observer.unobserve(currentRef);
          }
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: 0.5
        }
      );

      observer.observe(currentRef);

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      };
    },
    [authFormRef, authFormView, eventSource]
  );

  // Sync modal wrapper classes with current view (before paint to avoid flash)
  useLayoutEffect(
    () => {
      const genericModal = document.getElementById("generic-modal");
      if (!genericModal) return;

      const modalDialog = genericModal.querySelector(".modal-dialog");
      if (!modalDialog) return;

      const { currentView } = authFormView;

      if (currentView === FORM_VIEW.LOGIN) {
        genericModal.classList.remove("require-signup-open");
        modalDialog.classList.remove(
          "signup-modal-wrapper",
          "require-signup-modal"
        );
        modalDialog.classList.add("login-modal-wrapper");
        return;
      }

      modalDialog.classList.remove("login-modal-wrapper");

      if (currentView === FORM_VIEW.REGISTER) {
        // Small email signup: same as legacy
        genericModal.classList.remove("require-signup-open");
        modalDialog.classList.remove("require-signup-modal");
        modalDialog.classList.add("signup-modal-wrapper");
      } else {
        // New Auth Sign up
        genericModal.classList.add("require-signup-open");
        modalDialog.classList.add(
          "signup-modal-wrapper",
          "require-signup-modal"
        );
      }
    },
    [authFormView]
  );

  const RegisterEmailView = () => {
    return (
      <div className="signup-form-container">
        <Button
          className="back-button"
          variant={ButtonVariant.TERTIARY}
          onClick={() => handleFormViewUpdate(FORM_VIEW.REQUIRE_SIGNUP)}
          leadingIcon={IconNames.WpChevronLeft}
          label={trans("Back to all signup options")}
          fullWidth
        />
        <div className="panel-body">
          <EmailSignup nextUrl={nextUrl} />
        </div>
      </div>
    );
  };

  const LoginView = () => {
    return (
      <div className="login-form-container">
        <div className="login-modal-wrapper">
          <LoginForm
            toggleForm={() => handleFormViewUpdate(FORM_VIEW.REQUIRE_SIGNUP)}
            nextUrl={nextUrl}
            ref={authFormRef}
          />
        </div>
      </div>
    );
  };

  const SignupView = () => {
    return (
      <div className={"signup-prompt-container"}>
        <div className={"signup-prompt-main"}>
          <div className="signup-prompt-form" ref={authFormRef}>
            <SignupHeader />
            <AuthenticationOptions
              setAuthFormView={handleFormViewUpdate}
              nextUrl={nextUrl}
            />
          </div>
        </div>
        <div className="signup-prompt-expanded">
          <ConnectImage
            name={"auth/flying-book.svg"}
            title=""
            className={"signup-prompt-graphic"}
          />
          <Checklist showHeader={true} />
        </div>
      </div>
    );
  };

  return (
    <>
      {authFormView.currentView === FORM_VIEW.LOGIN && <LoginView />}

      {authFormView.currentView === FORM_VIEW.REQUIRE_SIGNUP && <SignupView />}

      {authFormView.currentView === FORM_VIEW.REGISTER && <RegisterEmailView />}
    </>
  );
};

RequireSignup.propTypes = {
  nextUrl: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  eventSource: PropTypes.shape({
    page: PropTypes.object,
    component: PropTypes.string
  })
};

export default RequireSignup;
